import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { Bell, Check } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';

interface Notif {
  id: string;
  agentId: string;
  kind: 'assigned' | 'client_reply' | 'mention' | 'system';
  title: string;
  body: string | null;
  ticketId: string | null;
  readAt: string | null;
  createdAt: string;
}

const POLL_MS = 60_000;

export function NotificationBell(): React.ReactElement | null {
  const [items, setItems] = useState<Notif[]>([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  async function load() {
    try {
      const r = await apiFetch<{ notifications: Notif[]; unread: number }>('/api/notifications');
      setItems(r.notifications);
      setUnread(r.unread);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return;
    }
  }

  useEffect(() => {
    void load();
    const id = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  async function markOne(n: Notif) {
    if (n.readAt) return;
    try {
      await apiFetch(`/api/notifications/${n.id}/read`, { method: 'POST' });
      await load();
    } catch {
      /* ignore */
    }
  }

  async function markAll() {
    try {
      await apiFetch('/api/notifications/read-all', { method: 'POST' });
      await load();
    } catch {
      /* ignore */
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        aria-label="Notifications"
        className="relative inline-flex items-center text-white/80 hover:text-white"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[var(--color-danger)] px-1 text-[10px] font-bold text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-40 mt-2 w-80 max-h-[480px] overflow-auto rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white text-[var(--color-body)] shadow-xl">
          <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-2 text-sm">
            <span className="font-semibold text-[var(--color-header)]">Notifications</span>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => void markAll()}
                className="inline-flex items-center gap-1 text-xs text-[var(--color-t3-blue)] hover:underline"
              >
                <Check className="h-3 w-3" /> Mark all read
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-[var(--color-muted)]">Nothing yet.</p>
          ) : (
            <ul>
              {items.map((n) => (
                <li
                  key={n.id}
                  className={
                    'border-b border-[var(--color-border)] px-4 py-2 ' +
                    (n.readAt ? '' : 'bg-[var(--color-t3-blue)]/5')
                  }
                >
                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      {n.ticketId ? (
                        <Link
                          to={`/tickets/${n.ticketId}`}
                          onClick={() => {
                            void markOne(n);
                            setOpen(false);
                          }}
                          className="block text-sm font-medium text-[var(--color-t3-blue)] hover:underline"
                        >
                          {n.title}
                        </Link>
                      ) : (
                        <span className="block text-sm font-medium text-[var(--color-header)]">{n.title}</span>
                      )}
                      {n.body && <div className="mt-0.5 text-xs text-[var(--color-muted)]">{n.body}</div>}
                      <div className="mt-0.5 text-[10px] text-[var(--color-muted)]">
                        {new Date(n.createdAt).toLocaleString()}
                      </div>
                    </div>
                    {!n.readAt && (
                      <button
                        type="button"
                        onClick={() => void markOne(n)}
                        title="Mark read"
                        className="text-[var(--color-muted)] hover:text-[var(--color-t3-blue)]"
                      >
                        <Check className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
